import { LibraryIcon } from '@components/Icons/LibraryIcon';
import { GradientScrollDiv } from '@components/util/GradientScrollDiv';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import {
  FOLDER_ROOT_PATH,
  folderColorValue,
  folderEntryPath,
  FolderPath,
  folderPathAncestry,
  folderPathName,
  LibraryFolderEntry,
} from '@lib/library/folderPath';
import { formatTimeAgoOrDate } from '@lib/utils/relativeTime';
import { pluralize } from '@lib/utils/units';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import { useLibrary } from '@stores/Library/Library';
import { LibraryFolderStore, useLibraryFolder } from '@stores/Library/LibraryFolder';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AllFoldersButton } from './AllFoldersButton';
import {
  folderDisplayName,
  folderUrl,
  moveFolderWithFeedback,
  moveMediaWithFeedback,
  useFollowFolderRelocation,
} from './FolderDialogs';
import { FolderGlyph } from './FolderGlyph';
import { FolderMenuButton } from './FolderMenuButton';
import css from './LibraryFolderBar.module.scss';
import { FolderDropHandlers, startFolderDrag, useFolderDropTarget } from './mediaDrag';

/** Whether the wrapped pills run onto a second line, i.e. a single row could not show them all. */
const wrapsOntoSecondLine = (element: HTMLElement) => {
  const first = element.firstElementChild;
  const last = element.lastElementChild;
  if (!first || !last) return false;
  return last.getBoundingClientRect().top >= first.getBoundingClientRect().bottom;
};

/**
 * Whether the folders don't fit on one line: wider than the row when it scrolls, onto a second
 * line when they wrap. Re-measured when the element changes size and after every render (a folder
 * added or filtered out changes the content without resizing the element). The "All folders"
 * button this drives can only narrow the folders when it appears, so the answer cannot flip back
 * and forth.
 */
const useFoldersOverflow = (wrapped: boolean) => {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [overflowing, setOverflowing] = useState(false);

  const measure = useCallback(() => {
    if (!element) return;
    setOverflowing(
      wrapped ? wrapsOntoSecondLine(element) : element.scrollWidth > element.clientWidth + 1,
    );
  }, [element, wrapped]);

  useLayoutEffect(measure);

  useEffect(() => {
    if (!element) return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, measure]);

  return { ref: setElement, overflowing };
};

/** The drop handlers every folder target shares. Stable, so the targets' handlers are too. */
const useFolderDropHandlers = (folder: LibraryFolderStore): FolderDropHandlers => {
  // A drag only moves the open folder's children, but its "Undo" can come after the user has
  // opened the folder it moved.
  const followRelocation = useFollowFolderRelocation(folder);
  return useMemo(
    () => ({
      onDropMedia: (mediaIds, target) =>
        void moveMediaWithFeedback(folder, mediaIds, target, 'drag'),
      onDropFolder: (path, target) =>
        void moveFolderWithFeedback(folder, path, target, 'drag', followRelocation),
    }),
    [folder, followRelocation],
  );
};

const FolderChip = observer(function FolderChip({
  entry,
  editable,
}: {
  entry: LibraryFolderEntry;
  editable: boolean;
}) {
  const folder = useLibraryFolder();
  const path = folderEntryPath(entry);
  const { mediaCount, lastModifiedAt } = folder.folderSummary(path);
  const handlers = useFolderDropHandlers(folder);
  const { isOver, dropHandlers } = useFolderDropTarget(path, handlers, editable);

  return (
    <div
      className={classNames(css.chip, isOver && css.dropOver, editable && css.withMenu)}
      style={{ '--folder-color': folderColorValue(entry.color) } as React.CSSProperties}
      // Dragged onto another pill or a breadcrumb to move it there. The link inside opts out, so
      // the drag carries the folder rather than the browser's own link drag.
      draggable={editable}
      onDragStart={editable ? (event) => startFolderDrag(event, path, entry.name) : undefined}
      {...dropHandlers}
    >
      <Link
        to={folderUrl(folder, path)}
        className={css.chipLink}
        title={[
          entry.name,
          mediaCount > 0 ? pluralize(mediaCount, 'media') : 'No media',
          lastModifiedAt !== undefined && `Last modified: ${formatTimeAgoOrDate(lastModifiedAt)}`,
        ]
          .filter(Boolean)
          .join('\n')}
        draggable={false}
      >
        <FolderGlyph color={entry.color} open={isOver} size={24} />
        <span className={css.chipName}>{entry.name}</span>
        {mediaCount > 0 && <span className={css.chipCount}>{mediaCount}</span>}
      </Link>
      {editable && <FolderMenuButton path={path} className={css.chipMenu} />}
    </div>
  );
});

/** A step back up the trail; media dropped on it move there. */
export const FolderCrumb = observer(function FolderCrumb({
  path,
  editable,
  className,
  showIcon = true,
}: {
  path: FolderPath;
  editable: boolean;
  className?: string;
  showIcon?: boolean;
}) {
  const folder = useLibraryFolder();
  const handlers = useFolderDropHandlers(folder);
  const { isOver, dropHandlers } = useFolderDropTarget(path, handlers, editable);
  const entry = folder.foldersByPath.get(path);

  return (
    <Link
      to={folderUrl(folder, path)}
      className={classNames(css.crumb, className, isOver && css.dropOver)}
      title={folderDisplayName(folder, path)}
      {...dropHandlers}
    >
      {showIcon &&
        (path === FOLDER_ROOT_PATH ? (
          <LibraryIcon size={16} />
        ) : (
          <FolderGlyph color={entry?.color} open={isOver} size={18} />
        ))}
      <span className={css.crumbName}>{folderDisplayName(folder, path)}</span>
    </Link>
  );
});

/**
 * The folders inside the open one, as a row of pills under the search line — the header's verb
 * pill, tinted with each folder's colour. The search filters them by name. The row scrolls
 * sideways, except in a folder with no media of its own, where the pills wrap so all of them show.
 *
 * The "New folder" button is on the search line above (`LibraryToolbar`), so the row shows only
 * while there are folders. On a phone it also carries the trail back up, which the desktop header
 * shows in its title.
 *
 * Rendered outside the grid's scroll container, so the virtualized grid's geometry is untouched
 * (see `.claude/docs/library-folders.md`).
 */
export const LibraryFolderBar = observer(function LibraryFolderBar() {
  const folder = useLibraryFolder();
  const library = useLibrary();
  const isMobile = useIsMobile();
  const current = folder.currentFolderPath;
  const children = folder.visibleChildFolders;
  const editable = !library.isReadOnly;
  // With no media beside them the folders are the page's content, so they wrap rather than hide
  // most of themselves behind a scroll.
  const wrapped = folder.holdsOnlyFolders;
  const { ref: chipsRef, overflowing } = useFoldersOverflow(wrapped);

  const showTrail = isMobile && current !== FOLDER_ROOT_PATH;
  const showChips = children.length > 0;
  if (!showTrail && !showChips) return null;

  const chips = children.map((entry) => (
    <FolderChip key={folderEntryPath(entry)} entry={entry} editable={editable} />
  ));

  return (
    <div className={css.bar}>
      {showTrail && (
        <nav className={css.trail} aria-label="Folder">
          {folderPathAncestry(current)
            .slice(0, -1)
            .map((path) => (
              <span key={path} className={css.trailStep}>
                <FolderCrumb path={path} editable={editable} />
                <ChevronRightRounded className={css.separator} fontSize="small" />
              </span>
            ))}
          <span className={css.trailCurrent} aria-current="page">
            <FolderGlyph color={folder.foldersByPath.get(current)?.color} open size={18} />
            <span className={css.crumbName}>{folderPathName(current)}</span>
            {editable && <FolderMenuButton path={current} />}
          </span>
        </nav>
      )}
      {showChips && (
        <div className={classNames(css.folderRow, wrapped && css.folderRowWrapped)}>
          {wrapped ? (
            <div ref={chipsRef} className={css.chipsWrapped}>
              {chips}
            </div>
          ) : (
            <GradientScrollDiv
              ref={chipsRef}
              horizontalScrolling
              horizontalArrows
              className={css.chipsRow}
              innerDivClassName={css.chips}
            >
              {chips}
            </GradientScrollDiv>
          )}
          {/* Only once one line can't show every folder: the way to one that is scrolled out of
              view, or anywhere else in the library, and the folder sort. */}
          {overflowing && (
            <div className={css.allFolders}>
              <AllFoldersButton />
            </div>
          )}
        </div>
      )}
    </div>
  );
});
